/**
 * The print-process set is drawn in one oblique projection. A world point
 * (x, h, z) — x to the right, h height above the table top, z depth toward the
 * back — lands on the stage at P = (x + 0.18z, 700 - h - 0.5z): top faces show
 * at 50 %, right side faces at 18 %, verticals stay vertical.
 *
 * Everything here is pure and evaluated once at module load: the scenes import
 * path strings, never compute geometry per frame.
 */

export const SHEAR = 0.18
export const DEPTH = 0.5
const BASE_Y = 700

const round = (n) => Math.round(n * 10) / 10

/** World (x, h, z) to stage [x, y]. */
export const P = (x, h, z) => [x + SHEAR * z, BASE_Y - h - DEPTH * z]

const pt = ([x, y]) => `${round(x)} ${round(y)}`

/** Closed polygon path through stage points. */
export const poly = (points) => `M${points.map(pt).join('L')}Z`

/** Open polyline path through stage points. */
export const line = (points) => `M${points.map(pt).join('L')}`

/**
 * A world-aligned box: top, front and right faces as path strings plus its
 * lit edges (front-top edge, left vertical) and its contact shadow footprint
 * offset (+12, -4) on the surface it stands on.
 */
export function box({ x0, x1, z0, z1, h0, h1 }) {
  const ftl = P(x0, h1, z0)
  const ftr = P(x1, h1, z0)
  const fbl = P(x0, h0, z0)
  const fbr = P(x1, h0, z0)
  const btl = P(x0, h1, z1)
  const btr = P(x1, h1, z1)
  const bbr = P(x1, h0, z1)
  const bbl = P(x0, h0, z1)
  const shift = ([x, y]) => [x + 12, y - 4]
  return {
    top: poly([ftl, ftr, btr, btl]),
    front: poly([ftl, ftr, fbr, fbl]),
    side: poly([ftr, btr, bbr, fbr]),
    litTop: line([ftl, ftr]),
    litLeft: line([ftl, fbl]),
    shadow: poly([fbl, fbr, bbr, bbl].map(shift)),
  }
}

/** Matrix that lays flat artwork (+y toward the viewer) on a horizontal face. */
export const face = (cx, cy, k = 1) =>
  `matrix(${round(k * 100) / 100} 0 ${-Math.round(SHEAR * k * 1000) / 1000} ${DEPTH * k} ${round(cx)} ${round(cy)})`

/* ---- Horizontal circles -------------------------------------------------
   A horizontal circle of radius r projects to an ellipse; its semi-axes and
   tilt come from the singular values of the face matrix [[1, -0.18], [0, 0.5]]. */
const A = 1 + SHEAR * SHEAR
const B = -SHEAR * DEPTH
const D = DEPTH * DEPTH
const ROOT = Math.sqrt(((A - D) / 2) ** 2 + B * B)
const L1 = (A + D) / 2 + ROOT
const L2 = (A + D) / 2 - ROOT
const TILT = (Math.atan2(L1 - A, B) * 180) / Math.PI - 180
const SX = Math.sqrt(L1)
const SY = Math.sqrt(L2)
/* Silhouette tangents of a vertical cylinder: where the ellipse is widest. */
const TAN_T = Math.atan(-SHEAR)
const TX = Math.cos(TAN_T) - SHEAR * Math.sin(TAN_T)
const TY = DEPTH * Math.sin(TAN_T)

/** Half-ellipse arc command to (x, y), r = circle radius, via the front (sweep 1) or back. */
const arc = (r, x, y, sweep) =>
  `A${round(SX * r)} ${round(SY * r)} ${round(TILT)} 0 ${sweep} ${round(x)} ${round(y)}`

/** Lower (front) edge of the projected circle at stage x, relative to its centre. */
export function frontY(x, r) {
  // Stage (x, y) comes from circle point (u, v) = (x + s·y, q·y), s = 0.18/0.5,
  // q = 1/0.5; u² + v² = r² gives (s² + q²)y² + 2s·x·y + x² - r² = 0.
  const s = SHEAR / DEPTH
  const q = 1 / DEPTH
  const qa = s * s + q * q
  const qb = 2 * s * x
  const qc = x * x - r * r
  const disc = Math.max(0, qb * qb - 4 * qa * qc)
  return (-qb + Math.sqrt(disc)) / (2 * qa)
}

/**
 * A round bore of radius r in a horizontal face, in FACE coordinates (draw it
 * inside a face matrix): the dark hole and the far inner wall, visible down
 * to a stage depth d. A stage drop d is a face shift (0.36 d, 2 d), so the
 * wall is the crescent of the rim circle outside its shifted copy.
 */
export function faceBore(r, d) {
  const cx = (SHEAR / DEPTH) * d
  const cy = d / DEPTH
  const dist = Math.hypot(cx, cy)
  const half = Math.sqrt(Math.max(0, r * r - (dist / 2) ** 2))
  const [ux, uy] = [cy / dist, -cx / dist]
  const a = [cx / 2 + half * ux, cy / 2 + half * uy]
  const b = [cx / 2 - half * ux, cy / 2 - half * uy]
  const p = ([x, y]) => `${round(x)} ${round(y)}`
  return {
    hole: `M${-r} 0A${r} ${r} 0 1 0 ${r} 0A${r} ${r} 0 1 0 ${-r} 0Z`,
    wall: `M${p(b)}A${r} ${r} 0 1 1 ${p(a)}A${r} ${r} 0 0 0 ${p(b)}Z`,
  }
}

/**
 * A vertical cylinder standing at local (0, 0) (centre of its foot ellipse),
 * radius r, height H. Returns the side-wall silhouette and flat vertical tone
 * bands between the given fractions of its width (0 = left tangent, 1 = right).
 */
export function cylinder(r, H, stops = []) {
  const xm = TX * r
  const ym = TY * r
  const body = `M${round(xm)} ${round(ym - H)}L${round(xm)} ${round(ym)}${arc(r, -xm, -ym, 1)}L${round(-xm)} ${round(-ym - H)}${arc(r, xm, ym - H, 0)}Z`
  const band = (f0, f1) => {
    const x0 = -xm + 2 * xm * f0
    const x1 = -xm + 2 * xm * f1
    const n = 8
    const xs = Array.from({ length: n + 1 }, (_, i) => x0 + ((x1 - x0) * i) / n)
    const upper = xs.map((x) => [x, frontY(x, r) - H])
    const lower = [...xs].reverse().map((x) => [x, frontY(x, r)])
    return poly([...upper, ...lower])
  }
  const bands = stops.map(([f0, f1]) => band(f0, f1))
  return {
    body,
    bands,
    xm,
    ym,
    rim: `M${round(-xm)} ${round(-ym - H)}${arc(r, xm, ym - H, 0)}`,
    foot: `M${round(-xm)} ${round(-ym)}${arc(r, xm, ym, 0)}`,
  }
}

/**
 * Several small vertical cylinders (pins, knobs) of radius r and height H
 * standing on the given stage foot points, merged into three paths: the
 * silhouettes, a lit band down their left side and the top ellipses.
 */
export function posts(r, H, feet) {
  const xm = TX * r
  const ym = TY * r
  const lit = 0.6 * xm
  const each = feet.map(([x, y]) => ({
    body: `M${round(x + xm)} ${round(y + ym - H)}L${round(x + xm)} ${round(y + ym)}${arc(r, x - xm, y - ym, 1)}L${round(x - xm)} ${round(y - ym - H)}${arc(r, x + xm, y + ym - H, 0)}Z`,
    band: `M${round(x - xm)} ${round(y - ym - H)}h${round(lit)}v${H}h${round(-lit)}Z`,
    top: `M${round(x - xm)} ${round(y - ym - H)}${arc(r, x + xm, y + ym - H, 1)}${arc(r, x - xm, y - ym - H, 1)}Z`,
  }))
  const join = (key) => each.map((part) => part[key]).join('')
  return { body: join('body'), band: join('band'), top: join('top') }
}
